import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getBackendUrl } from '../../helpers/url';
import { isAuthEnabled, getToken } from '../../helpers/auth';
import ToggleDashboardButton from '../../components/ToggleDashboardButton';
import { useSocketState } from '../../helpers/useSocket';
import LiveGameStatRow from '../../components/LiveGame/LiveGameStatRow';
import LiveGameStatDivider from '../../components/LiveGame/LiveGameStatDivider';
import LiveGameTechList from '../../components/LiveGame/LiveGameTechList';

const GRAPH_BUTTONS = [
	{ graph: 'EconomyGraph', label: 'Economy', icon: null },
	{ graph: 'MilitaryGraph', label: 'Military', icon: null },
	{ graph: 'ScoreGraph', label: 'Score', icon: null },
	{ graph: 'DestroyedValueGraph', label: 'Destroyed\nValue', icon: null },
	{ graph: 'TotalResPerMinGraph', label: 'Res/Min', icon: null },
	{ graph: 'TotalResGraph', label: 'Total Res', icon: null },
	{ graph: 'TotalGatheredGraph', label: 'Gathered', icon: null },
	{ graph: 'ArmyValueGraph', label: 'Army Value', icon: null },
	{ graph: 'TotalTCIdleTimeGraph', label: 'TC Idle', icon: null },
	{ graph: 'TotalVillagersIdleTimeGraph', label: 'Vill Idle', icon: null },
	{ graph: 'RelicGraph', label: 'Relics', icon: null },
];

const RESET_BUTTONS = [
	{ key: 'resetGraphs', label: 'Reset\nGraphs', icon: null },
	{ key: 'resetRelics', label: 'Reset\nRelics', icon: null },
	{ key: 'resetUnitTracker', label: 'Reset\nTrackers', icon: null },
];

function tokenParam(prefix = '?') {
	return isAuthEnabled() && getToken() ? `${prefix}token=${getToken()}` : '';
}

function LiveGameDashboard() {
	const { socket } = useOutletContext();

	// Toggle state for graph buttons — mirrors liveGameState.liveGameOverlay
	const [overlayState, setOverlayState] = useState({});

	// Toggle state for sheep/overview/minimap
	const [showSheep, setShowSheep] = useState(false);
	const [showOverview, setShowOverview] = useState(false);
	const [showMinimap, setShowMinimap] = useState(false);

	const [gameState] = useSocketState('LiveGameOverlay')

	// Fetch initial state for all toggles
	useEffect(() => {
		fetch(`${getBackendUrl()}/api/liveGame/liveGameState${tokenParam()}`)
			.then(r => r.json())
			.then(data => {
				setOverlayState(data.liveGameOverlay ?? {});
				setShowSheep(data.sheep?.showSheepPopup ?? false);
				setShowOverview(data.liveGameOverlay?.showOverview ?? false);
				setShowMinimap(data.liveGameOverlay?.showMinimap ?? false);
			})
			.catch(err => console.error('liveGameState fetch error', err));
	}, []);

	// Per-graph toggle socket events
	useEffect(() => {
		const handlers = GRAPH_BUTTONS.map(({ graph }) => {
			const handler = (value) => setOverlayState(prev => ({ ...prev, [`show${graph}`]: value }));
			socket.on(`toggle${graph}`, handler);
			return { graph, handler };
		});
		return () => handlers.forEach(({ graph, handler }) => socket.off(`toggle${graph}`, handler));
	}, [socket]); // eslint-disable-line react-hooks/exhaustive-deps

	// Sheep / overview / minimap socket events
	useEffect(() => {
		const onSheep = (data) => setShowSheep(data?.showSheepPopup ?? false);
		const onOverview = (value) => setShowOverview(value);
		const onMinimap = (value) => setShowMinimap(value);

		socket.on('updateSheep', onSheep);
		socket.on('updateOverview', onOverview);
		socket.on('toggleMinimap', onMinimap);

		return () => {
			socket.off('updateSheep', onSheep);
			socket.off('updateOverview', onOverview);
			socket.off('toggleMinimap', onMinimap);
		};
	}, [socket]); // eslint-disable-line react-hooks/exhaustive-deps

	function apiUrl(path) {
		return `${getBackendUrl()}/api/liveGame/${path}${tokenParam()}`;
	}

	function graphUrl(graphName) {
		return `${getBackendUrl()}/api/liveGame/toggleGraph?graph=${graphName}${tokenParam('&')}`;
	}

	function apiGet(path) {
		fetch(apiUrl(path)).catch(err => console.error(`${path} error`, err));
	}

	function toggleGraph(graphName) {
		fetch(graphUrl(graphName), { method: 'POST' })
			.catch(err => console.error('toggleGraph error', err));
	}

	return (
		<div className='border-solid border-zinc-600 border-2 flex flex-row p-3 gap-4'>
			<div className='flex flex-col gap-4'>
				<div>
					<h2 className='text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2'>Graph Toggles</h2>
					<div className='flex gap-2 flex-wrap w-96'>
						{GRAPH_BUTTONS.map(({ graph, label, icon }) => (
							<ToggleDashboardButton
								key={graph}
								label={label}
								icon={icon}
								isActive={overlayState[`show${graph}`] ?? false}
								onClick={() => toggleGraph(graph)}
								copyUrl={graphUrl(graph)}
							/>
						))}
					</div>
				</div>

				<div>
					<h2 className='text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2'>Overlay Toggles</h2>
					<div className='flex gap-2 flex-wrap'>
						<ToggleDashboardButton
							label='Sheep'
							icon={null}
							isActive={showSheep}
							onClick={() => apiGet('toggleSheep')}
							copyUrl={apiUrl('toggleSheep')}
						/>
						<ToggleDashboardButton
							label='Overview'
							icon={null}
							isActive={showOverview}
							onClick={() => apiGet('toggleOverview')}
							copyUrl={apiUrl('toggleOverview')}
						/>
						<ToggleDashboardButton
							label='Minimap'
							icon={null}
							isActive={showMinimap}
							onClick={() => apiGet('toggleMinimap')}
							copyUrl={apiUrl('toggleMinimap')}
						/>
					</div>
				</div>

				<div>
					<h2 className='text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2'>Reset</h2>
					<div className='flex gap-2 flex-wrap'>
						{RESET_BUTTONS.map(({ key, label, icon }) => (
							<ToggleDashboardButton
								key={key}
								label={label}
								icon={icon}
								variant='push'
								onClick={() => apiGet(key)}
								copyUrl={apiUrl(key)}
							/>
						))}
					</div>
				</div>
			</div>

			<div className='flex flex-col gap-1'>
				<span className='font-bold'><LiveGameStatRow gameState={gameState} label='' path='name' /></span>
				<LiveGameStatRow gameState={gameState} label='Civilization' path='civilization' />
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label='Sheep' icon='/sheepCounter/sheepIcon.png' path='sheep.count' precision='int'/>
				<LiveGameStatRow gameState={gameState} label='Economy' icon='/liveGameIcons/worker_count.png' path='totalEconomy' altPath="idleEconomy" precision='int'/>
				<LiveGameStatRow gameState={gameState} label='Army' icon='/liveGameIcons/resource_military.png' path='militaryCount' precision='int'/>
				<LiveGameStatRow gameState={gameState} label='Population' icon='/liveGameIcons/popcap.png' path='currentPopulation' altPath="maxPopulation" precision='int'/>
				<LiveGameStatRow gameState={gameState} label='Relic' icon='/liveGameIcons/relics.png' path='relics' altPath="relicsCarried" precision='int'/>
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label='Food' icon='/liveGameIcons/food.png' path='resources.foodCurrent' altPath='resources.foodWorker' precision='int'/>
				<LiveGameStatRow gameState={gameState} label='Food Total' icon='/liveGameIcons/food.png' path='total_resources.food' precision='decimal'/>
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label='Wood' icon='/liveGameIcons/wood.png' path='resources.woodCurrent' altPath='resources.woodWorker' precision='int'/>
				<LiveGameStatRow gameState={gameState} label='Wood Total' icon='/liveGameIcons/wood.png' path='total_resources.wood' precision='decimal'/>
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label='Gold' icon='/liveGameIcons/gold.png' path='resources.goldCurrent' altPath='resources.goldWorker' precision='int'/>
				<LiveGameStatRow gameState={gameState} label='Gold Total' icon='/liveGameIcons/gold.png' path='total_resources.gold' precision='decimal'/>
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label='Stone' icon='/liveGameIcons/stone.png' path='resources.stoneCurrent' altPath='resources.stoneWorker' precision='int'/>
				<LiveGameStatRow gameState={gameState} label='Stone Total' icon='/liveGameIcons/stone.png' path='total_resources.stone' precision='decimal'/>
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label='Olive/Silver' path='resources.oliveCurrent' precision='decimal'/>
				<LiveGameStatRow gameState={gameState} label='Stone Total'  path='total_resources.merc_byz' precision='decimal'/>
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label='Total Collected' path='total_resources.total' precision='int'/>
				<LiveGameStatRow gameState={gameState} label='Army Value' icon='/liveGameIcons/resource_military.png' path='armyValue' precision='int'/>
				<LiveGameStatRow gameState={gameState} label='Eco Kills' icon='/liveGameIcons/worker_count.png' path='worker_kills' precision='int'/>
				<LiveGameStatRow gameState={gameState} label='Destroyed' path='destroyed_value' precision='int'/>
				
			</div>

			<div className='flex flex-col gap-1'>
				
			</div>
		</div>
	);
}

export default LiveGameDashboard;
